import * as React from 'react';

import { AccountBalance as BaseAccountBalance } from '@rematter/modules/finance/types';
import { Button, DataGridColumn, Money, Tooltip } from '@rematter/ui-web';
import { LucideBanknoteArrowDown, LucideBanknoteArrowUp } from 'lucide-react';

import { useTranslations } from '@/finance/i18n/useTranslations';

type AccountBalance = BaseAccountBalance & {
  // Required for the 'actions' column
  actions?: never;
};

export const useBalancesListColumns = ({
  onNetPay,
}: {
  onNetPay: (id: string, name: string) => void;
}) => {
  const t = useTranslations();

  const handlePay = React.useCallback(
    (id: string, name: string) => {
      onNetPay(id, name);
    },
    [onNetPay],
  );

  const columns: DataGridColumn<AccountBalance>[] = React.useMemo(
    () => [
      {
        key: 'name',
        header: t.columns.accountName,
        grow: 1,
        cell: ({ row: record }) => String(record.name),
      },
      {
        key: 'pendingTransactions',
        header: t.columns.pendingTransactions,
        cell: ({ row: record }) => <span>{record.pendingTransactions}</span>,
      },
      {
        key: 'balances',
        header: t.columns.balancesColumn,
        cell: ({ row: record }) => (
          <div className="flex flex-col gap-1">
            {record.balances.map((balance) => (
              <div key={balance.currencyCode} className="flex justify-between">
                <span className="font-medium">{balance.currencyCode}</span>
                <span
                  className={
                    balance.amount < 0 ? 'text-failure/60' : 'text-success/60'
                  }
                >
                  <Money
                    amount={balance.amount}
                    currencyCode={balance.currencyCode}
                  />
                </span>
              </div>
            ))}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row: record }) => (
          <div className="flex flex-col gap-1">
            {record.balances.map((balance) => (
              <Tooltip
                key={balance.currencyCode}
                tip={
                  balance.amount > 0
                    ? t.balances.list.buttons.receive(balance.currencyCode)
                    : t.balances.list.buttons.pay(balance.currencyCode)
                }
                wrapWithButton={false}
              >
                <Button
                  variant="outline"
                  color={balance.amount > 0 ? 'success' : 'failure'}
                  onPress={() => handlePay(record.id, record.name)}
                  size="icon"
                  icon={
                    balance.amount > 0 ? (
                      <LucideBanknoteArrowUp className="size-4" />
                    ) : (
                      <LucideBanknoteArrowDown className="size-4" />
                    )
                  }
                />
              </Tooltip>
            ))}
          </div>
        ),
      },
    ],
    [t, handlePay],
  );

  return { columns };
};
